import { Container, Box, Text } from "@chakra-ui/react";
import { FC, useCallback, useMemo, useState } from "react";
import { DndGroup } from "./dndGroup";
import { DndDropZone } from "./dndDropZone";
import {
  updateSingleItem,
  groupSelectedItems,
  moveMultipleAssets,
  Path,
  ungroupSelectedItems,
  ItemLevel,
  formatPath,
  isMovingItemUpwards
} from "./helpers";
import { SingleAsset } from "./singleAsset";
import { usePackAssets, AssetGroup as AssetGroupType, Asset as AssetType, ItemLevels, usePack } from "lib/hooks";
import { DndToolbar } from "./toolbar";
import { DndProvider } from "./context";
import { UploadFileInProgress, usePackContext, persistUpdatedLayout } from "domains/pack";
import { useAssetSelectionContext } from "domains/interactions/context/assetSelectionContext";
import { useRunSync } from "lib/hooks";
import { flattenLayout, formatData } from "domains/pack/utils";

export type SelectedAssets = string[];

type DropZone = {
  path: string;
  childrenCount: number;
}

type Item = {
  path: string;
  id: string;
  level: 'group' | 'row' | 'asset';
  selectedAssets: SelectedAssets;
  type: keyof typeof ItemLevel;
}

const calcSize = (elem: any) => {
  let count = 0;

  if (elem.children) {
    for (const child of elem.children) {
      count += calcSize(child);
    }
  }
  else {
    return 1;
  }

  return count;
}

const generateOptimistic = (data: any, movedAssets: any, sourcePath: Path, targetPath: Path) => {
  let pos = 0;

  let target = data;
  const max = targetPath.length-1;

  const isMovingItemUp = isMovingItemUpwards(sourcePath, targetPath);

  for (let x = 0; x <= max; x++) {
    let idx = parseInt(targetPath[x]);

    if (x === sourcePath.length-1 && !isMovingItemUp) {
      idx--;
    }

    for (let x = 0; x < idx; x++) {
      pos += calcSize(target[x]);
    }

    if (x < max) {
      target = target[idx].children;
    }
  }

  const flat = flattenLayout(data);

  for (const asset of movedAssets) {
    flat.splice(pos++, 0, asset);
  }

  return formatData(flat);
}

export const ItemDnD: FC = () => {
  const { packId } = usePackContext();
  const { data: pack } = usePack(packId);
  const { selectedAssets, setSelectedAssets, resetSelectedItems } = useAssetSelectionContext();
  const [ isDragInProgress, setIsDragInProgress ] = useState<boolean>(false);
  const { data, mutate } = usePackAssets(pack);
  const { sync } = useRunSync(packId);

  const handleDrop = useCallback(
    async (dropZone: DropZone, item: Item): Promise<void> => {
      const splitDropZonePath: Path = formatPath(dropZone.path);
      const splitItemPath: Path = formatPath(item.path);

      const isMovingMultipleAssets: boolean = selectedAssets.length > 1;

      if (isMovingMultipleAssets) {
        const [ updated, movedAssets ] = moveMultipleAssets(data, selectedAssets, splitDropZonePath);

        const optimistic = generateOptimistic(updated, movedAssets, splitItemPath, splitDropZonePath);

        setSelectedAssets([]);
        await mutate(optimistic, false);
        await persistUpdatedLayout(packId, updated, movedAssets);
      }

      if (!isMovingMultipleAssets) {
        const [ updated, movedAsset ] = updateSingleItem(data, splitDropZonePath, splitItemPath);

        const optimistic = generateOptimistic(updated, movedAsset, splitItemPath, splitDropZonePath);

        setSelectedAssets([]);
        await mutate(optimistic, false);
        await persistUpdatedLayout(packId, updated, movedAsset);
      }

      await sync();
      return resetSelectedItems();
    },
    [ packId, resetSelectedItems, mutate, sync, data, selectedAssets ]
  );

  const onSelectItem = (e, path): void => {
    e.stopPropagation();
    const selectMultipleKey = e.ctrlKey || e.metaKey || e.shiftKey;

    if (!selectMultipleKey) {
      setSelectedAssets([path]);
      return;
    }

    if (selectedAssets.includes(path)) {
      return setSelectedAssets(() => {
        const selected = selectedAssets.filter((asset) => asset !== path);
        // TODO: Type declarations
        return selected.sort((a: any, b: any) =>  b[0] - a[0]);
      })
    }

    if (selectMultipleKey) {
      return setSelectedAssets(() => {
        const selected = [ ...selectedAssets, path ];
        return selected.sort((a, b) =>  b[0] - a[0]);
      });
    }

    if (!selectMultipleKey) {
      return setSelectedAssets(() => [ path ]);
    }
  };

  const groupItems = async () => {
    const [ updated, movedAssets ] = groupSelectedItems(data, selectedAssets);

    await mutate([ ...updated], false);
    await mutate(() => persistUpdatedLayout(packId, updated, movedAssets));

    return resetSelectedItems();
  };

  const ungroupItems = async () => {
    const [ updated, movedAssets ] = ungroupSelectedItems(data, selectedAssets[0]);

    await mutate([ ...updated], false);
    await mutate(() => persistUpdatedLayout(packId, updated, movedAssets));

    return resetSelectedItems();
  };

  const renderItem = (item, index: number) => {
    switch (item.level) {
      case ItemLevels.GROUP:
        return renderGroup(item, index);
      default:
        return renderAsset(item, index);
    }
  }

  const renderGroup = (item: AssetGroupType, index: number) => (
    <DndGroup
      key={`${item.id}-group`}
      data={item}
      handleDrop={handleDrop}
      onSelectItem={onSelectItem}
      selectedAssets={selectedAssets}
      path={`${index}`}
      layout={data}
    />
  )

  const renderAsset = (item: AssetType, index: number) => (
    <SingleAsset
      key={`${item.id}-asset`}
      data={item}
      selectedAssets={selectedAssets}
      path={`${index}`}
    />
  );

  const isMultipleSelected = useMemo(() => {
    if (!data || selectedAssets.length > 1) {
      return true;
    }

    if (selectedAssets.length === 0) {
      return false;
    }

    let isMultipleSelected = false;

    for (let path of selectedAssets) {
      const splitPath = path.split('-');

      if (splitPath.length === 1) {
        isMultipleSelected = data[splitPath[0]]?.level === ItemLevel.GROUP;
      }
    }

    return isMultipleSelected;
  }, [data, selectedAssets]);

  // TODO: Implement skeleton loading ui
  if (!data) {
    return null;
  }

  return (
    <DndProvider
      value={{
        onSelectItem,
        isMultipleSelected,
        resetSelectedItems,
        isDragInProgress,
        setIsDragInProgress,
      }}
    >
      <Container maxWidth={920} width="100%" padding={0} margin={'0 auto'} position="relative">
        {data.length < 1 && (
          <Box maxWidth={"750px"} margin={"0 auto"}>
            <Box display={['none', 'block']}><Text fontSize={["large", "x-large"]} textAlign={"center"} color={"gray.500"}>Let’s control the clutter. Just paste your link. Drag and drop your file. Or simply start typing.</Text></Box>
            <Box display={['block', 'none']}><Text fontSize={["large", "x-large"]} textAlign={"center"} color={"gray.500"}>Let’s control the clutter. To add a link, file, text or image to this pack, click the &quot;+&quot; button below.</Text></Box>
          </Box>
        )}
        {data.map((item, index) => (
          <Box key={item.id}>
            <DndDropZone
              layout={data}
              data={{ path: `${index}`, childrenCount: data.length }}
              onDrop={handleDrop}
              path={`${index}`}
            />
            {renderItem(item, index)}
          </Box>
        ))}
        <DndDropZone
          layout={data}
          data={{ path: `${data.length}`, childrenCount: data.length }}
          onDrop={handleDrop}
        />
        <UploadFileInProgress />
      </Container>
      <DndToolbar layout={data} selectedAssets={selectedAssets} groupItems={groupItems} ungroupItems={ungroupItems} />
    </DndProvider>
  );
};
